'use client';

import React, { forwardRef, useEffect, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { Cart } from 'assets/icons';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { PrimaryButton } from 'components/shared/PrimaryButton';
import {
  LeftPanel,
  MidPanel,
  MidPanelPhone as BaseMidPanelPhone,
  RightPanel,
} from 'components/shared/BottomBar';
import { getCurrencySymbolFromString } from 'lib/region/countryUtils';
import { useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import { Currency } from 'utils/Currency';
import styles from './ShopBottomBar.module.css';

const cx = (...c: (string | undefined)[]) => c.filter(Boolean).join(' ');

const BottomBarContainer = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function BottomBarContainer({ className, ...rest }, ref) {
  return (
    <div
      ref={ref}
      className={cx(styles.bottomBarContainer, 'u-grid', className)}
      {...rest}
    />
  );
});

const MidPanelPhone = ({
  className,
  ...props
}: React.ComponentProps<typeof BaseMidPanelPhone>) => (
  <BaseMidPanelPhone className={cx(styles.midPanelPhone, className)} {...props} />
);

const DesktopLeftPanel = ({
  className,
  ...props
}: React.ComponentProps<typeof LeftPanel>) => (
  <LeftPanel className={cx(styles.desktopLeftPanel, className)} {...props} />
);

const DesktopMidPanel = ({
  className,
  ...props
}: React.ComponentProps<typeof MidPanel>) => (
  <MidPanel className={cx(styles.desktopMidPanel, className)} {...props} />
);

const DesktopRightPanel = ({
  className,
  ...props
}: React.ComponentProps<typeof RightPanel>) => (
  <RightPanel className={cx(styles.desktopRightPanel, className)} {...props} />
);

const ItemInfo = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cx(styles.itemInfo, props.className)} />
);

const CartItemCount = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cx(styles.cartItemCount, props.className)} />
);

const MobileTopRow = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cx(styles.mobileTopRow, props.className)} />
);

const PriceInfoContainer = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cx(styles.priceInfoContainer, props.className)} />
);

const PriceDetailsContainer = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cx(styles.priceDetailsContainer, props.className)} />
);

const CartTotal = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cx(styles.cartTotal, props.className)} />
);

const STICKY_BOTTOM_BAR_OFFSET_VAR = '--sticky-bottom-bar-offset';

const ShopBottomBar: React.FC = () => {
  const barRef = useRef<HTMLDivElement>(null);
  const t = useTranslations('cart');
  const tRing = useTranslations('ring');
  const { cart, showCart, taxes, duty, currency } = useCartStore(
    useShallow((s) => ({
      cart: s.cart,
      showCart: s.showCart,
      taxes: s.taxes,
      duty: s.duty,
      currency: s.currency,
    })),
  );

  const itemCount = useMemo(() => {
    if (!cart?.lines?.edges) return 0;
    return cart.lines.edges.reduce(
      (sum: number, edge: { node: { quantity: number } }) =>
        sum + edge.node.quantity,
      0,
    );
  }, [cart]);

  const cartTotal = useMemo(() => {
    if (!cart?.cost?.totalAmount?.amount) return 0;
    return (
      parseFloat(cart.cost.totalAmount.amount as string) -
      (taxes ?? 0) -
      (duty ?? 0)
    );
  }, [cart, taxes, duty]);

  const currencySymbol = useMemo(() => {
    const code = cart?.cost?.subtotalAmount?.currencyCode as string | undefined;
    if (code) return getCurrencySymbolFromString(code);
    return getCurrencySymbolFromString((currency ?? Currency.usd) as string);
  }, [cart, currency]);
  const formatPriceWithComma = useFormatPrice();

  useEffect(() => {
    if (itemCount === 0) {
      document.documentElement.style.removeProperty(STICKY_BOTTOM_BAR_OFFSET_VAR);
      return;
    }

    const updateOffset = () => {
      const height = barRef.current?.offsetHeight ?? 0;
      document.documentElement.style.setProperty(
        STICKY_BOTTOM_BAR_OFFSET_VAR,
        `${height}px`,
      );
    };

    updateOffset();

    const observer = new ResizeObserver(updateOffset);
    const node = barRef.current;
    if (node) observer.observe(node);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty(STICKY_BOTTOM_BAR_OFFSET_VAR);
    };
  }, [itemCount]);

  if (itemCount === 0) return null;

  const handleViewCart = () => {
    showCart();
  };

  const viewCartLabel = t('sharedCart.shopBottomBar.viewCart');

  return (
    <BottomBarContainer ref={barRef} data-testid="shop-bottom-bar">
      <MidPanelPhone>
        <MobileTopRow>
          <CartItemCount>
            <Cart aria-hidden />
            <p>
              {tRing('ring.shopBottomBar.itemsAdded', { count: itemCount })}
            </p>
          </CartItemCount>
          <PriceDetailsContainer>
            <CartTotal>
              {currencySymbol}
              {formatPriceWithComma(cartTotal)}
            </CartTotal>
          </PriceDetailsContainer>
        </MobileTopRow>
        <PrimaryButton
          type="button"
          className="gtm"
          data-buttontype="shop_buy_view_cart"
          data-testid="shop-view-cart"
          onClick={handleViewCart}
          aria-label={viewCartLabel}
        >
          {viewCartLabel}
        </PrimaryButton>
      </MidPanelPhone>

      <DesktopLeftPanel style={{ gridColumn: '1 / 6' }}>
        <ItemInfo>
          <CartItemCount>
            <Cart aria-hidden />
            <p>
              {tRing('ring.shopBottomBar.itemsAdded', { count: itemCount })}
            </p>
          </CartItemCount>
        </ItemInfo>
      </DesktopLeftPanel>

      <DesktopMidPanel>
        <PriceDetailsContainer>
          <CartTotal>
            {currencySymbol}
            {formatPriceWithComma(cartTotal)}
          </CartTotal>
        </PriceDetailsContainer>
      </DesktopMidPanel>

      <DesktopRightPanel>
        <PriceInfoContainer>
          <PrimaryButton
            type="button"
            className="gtm"
            data-buttontype="shop_buy_view_cart"
            data-testid="shop-view-cart"
            onClick={handleViewCart}
            aria-label={viewCartLabel}
          >
            {viewCartLabel}
          </PrimaryButton>
        </PriceInfoContainer>
      </DesktopRightPanel>
    </BottomBarContainer>
  );
};

export default ShopBottomBar;
